import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useDeleteRecipientMutation, useUpdateRecipientNicknameMutation } from '@/api/v1';
import CloseIcon from '@/assets/icons/close.svg?react';
import TrashIcon from '@/assets/icons/trash.svg?react';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { ContactDetailsSection } from '@/components/molecules/ContactDetailsSection';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { ContactDetailsSectionDisplayableEntry } from '@/types/contactDetailsSection';
import { CryptoRecipientStatus, RecipientResponse } from '@/types/recipient';
import {
  getRecipientAccountTypeLabel,
  getRecipientDisplayEntries,
  getRecipientDisplayName,
} from '@/utility/recipientDisplayAdapter';

interface Props {
  readonly paymentMethod: RecipientResponse;
}

const NICKNAME_MAX_LENGTH = 40;

export const FiatPaymentMethodCard: React.FC<Props> = (props: Props): React.ReactElement => {
  const { paymentMethod } = props;

  const tr = useTranslator();
  const toast = useToast();

  const [deleteRecipient, deleteRecipientMutation] = useDeleteRecipientMutation();
  const [updateRecipientNickname, updateRecipientNicknameMutation] =
    useUpdateRecipientNicknameMutation();
  const [deleteConfirm, setDeleteConfirm] = React.useState<boolean>(false);

  // Nickname editing state
  const [isEditingNickname, setIsEditingNickname] = React.useState<boolean>(false);
  const [nicknameValue, setNicknameValue] = React.useState<string>(paymentMethod.nickname ?? '');

  // Sync nickname value when paymentMethod changes (e.g., after save)
  React.useEffect((): void => {
    setNicknameValue(paymentMethod.nickname ?? '');
  }, [paymentMethod.nickname]);

  React.useEffect((): void => {
    switch (deleteRecipientMutation.status) {
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      case QueryStatus.fulfilled:
        toast.show(tr('DELETE_PAYMENT_METHOD_SUCCESS'), 'success');
        setDeleteConfirm(false);
        break;
      default:
        // Ignore
        break;
    }
  }, [deleteRecipientMutation.status, toast, tr]);

  const onDeleteConfirm = React.useCallback((): void => {
    setDeleteConfirm(true);
  }, []);

  const onDeleteConfirmed = React.useCallback((): void => {
    deleteRecipient(paymentMethod.id);
  }, [deleteRecipient, paymentMethod.id]);

  const onDeleteAborted = React.useCallback((): void => {
    setDeleteConfirm(false);
  }, []);

  // Nickname handlers
  const onStartEditNickname = React.useCallback((): void => {
    setIsEditingNickname(true);
  }, []);

  const onCancelEditNickname = React.useCallback((): void => {
    setIsEditingNickname(false);
    setNicknameValue(paymentMethod.nickname ?? '');
  }, [paymentMethod.nickname]);

  const onSaveNickname = React.useCallback((): void => {
    const trimmedNickname = nicknameValue.trim();

    // Client-side validation for special characters (skip if clearing nickname)
    if (
      trimmedNickname !== null &&
      trimmedNickname !== '' &&
      !/^[a-zA-Z0-9\s\-_]+$/.test(trimmedNickname)
    ) {
      toast.show(tr('NICKNAME_INVALID_CHARACTERS'), 'error');
      return;
    }

    // Send empty string to clear nickname (backend converts '' to null)
    updateRecipientNickname({
      id: paymentMethod.id,
      data: { nickname: trimmedNickname },
    })
      .unwrap()
      .then((): void => {
        toast.show(tr('NICKNAME_UPDATED_SUCCESS'), 'success');
        setIsEditingNickname(false);
      })
      .catch((error: unknown): void => {
        // Check if error message indicates invalid characters
        const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? '';
        if (
          errorMessage.toLowerCase().includes('character') ||
          errorMessage.toLowerCase().includes('invalid')
        ) {
          toast.show(tr('NICKNAME_INVALID_CHARACTERS'), 'error');
        } else {
          toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        }
        setNicknameValue(paymentMethod.nickname ?? '');
      });
  }, [nicknameValue, paymentMethod.id, paymentMethod.nickname, updateRecipientNickname, toast, tr]);

  const onNicknameChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    setNicknameValue(event.target.value);
  }, []);

  const onClearNickname = React.useCallback((): void => {
    setNicknameValue('');
  }, []);

  const onNicknameKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        onSaveNickname();
      } else if (event.key === 'Escape') {
        event.stopPropagation();
        onCancelEditNickname();
      }
    },
    [onSaveNickname, onCancelEditNickname],
  );

  const accountTypeEntry: ContactDetailsSectionDisplayableEntry = {
    key: 'accountType',
    displayValue: tr(getRecipientAccountTypeLabel(paymentMethod)),
  };

  const bankAccountEntries = React.useMemo(
    (): readonly ContactDetailsSectionDisplayableEntry[] =>
      getRecipientDisplayEntries(paymentMethod).map(
        (entry: ContactDetailsSectionDisplayableEntry): ContactDetailsSectionDisplayableEntry =>
          entry.label
            ? { ...entry, label: tr(entry.label), displayValue: tr(entry.displayValue) }
            : entry,
      ),
    [paymentMethod, tr],
  );

  const isUpdating = updateRecipientNicknameMutation.status === QueryStatus.pending;

  return (
    <Paper elevation={3}>
      <Box borderRadius={2} px={3} py={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mt="2px" mr="-4px">
          <Typography variant="body2" color="text.primary" fontWeight={500}>
            {getRecipientDisplayName(paymentMethod)}
          </Typography>
          {!paymentMethod.belongsToBeneficiary && (
            <IconButton sx={styles.iconButton} size="small" onClick={onDeleteConfirm}>
              <TrashIcon fill="currentColor" />
            </IconButton>
          )}
        </Box>

        {paymentMethod.cryptoStatus != null && (
          <Box
            mt={1}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            height={32}
            px={1}
            borderRadius={2}
            bgcolor={cryptoStatusColors[paymentMethod.cryptoStatus].background}>
            <Typography
              variant="body1"
              color={cryptoStatusColors[paymentMethod.cryptoStatus].foreground}>
              {tr(cryptoStatusLabels[paymentMethod.cryptoStatus])}
            </Typography>
          </Box>
        )}

        {/* Nickname section */}
        <Box mt={1.5}>
          {isEditingNickname ? (
            <Box display="flex" flexDirection="column">
              <TextField
                size="small"
                value={nicknameValue}
                onChange={onNicknameChange}
                onKeyDown={onNicknameKeyDown}
                placeholder={tr('NICKNAME_PLACEHOLDER')}
                inputProps={{ maxLength: NICKNAME_MAX_LENGTH }}
                autoFocus
                disabled={isUpdating}
                fullWidth
                sx={styles.nicknameInput}
                InputProps={{
                  endAdornment: nicknameValue.length > 0 && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={onClearNickname}
                        disabled={isUpdating}
                        sx={styles.clearButton}>
                        <CloseIcon fill="currentColor" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {nicknameValue.length}/{NICKNAME_MAX_LENGTH}
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={onSaveNickname}
                    disabled={isUpdating}>
                    {tr('SAVE')}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={onCancelEditNickname}
                    disabled={isUpdating}>
                    {tr('CANCEL')}
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : paymentMethod.nickname ? (
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="body1" color="custom.LightGray">
                  {tr('NICKNAME_PLACEHOLDER')}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {paymentMethod.nickname}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                onClick={onStartEditNickname}
                sx={styles.editNicknameButton}>
                {tr('EDIT_NICKNAME')}
              </Button>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {tr('NICKNAME')}
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={onStartEditNickname}
                sx={styles.addNicknameButton}>
                {tr('ADD_NICKNAME')}
              </Button>
            </Box>
          )}
        </Box>

        <Box mt="18px" display="flex" flexDirection="column" gap={2}>
          <ContactDetailsSection title={tr('ACCOUNT_TYPE')} values={[accountTypeEntry]} />
          <ContactDetailsSection
            title={tr('BANK_ACCOUNT_INFORMATION')}
            values={bankAccountEntries}
          />
        </Box>

        <ConfirmationModal
          open={deleteConfirm}
          onAbort={onDeleteAborted}
          onConfirm={onDeleteConfirmed}
          confirmButtonText={tr('DELETE_ACCOUNT')}
          abortButtonText={tr('KEEP_ACCOUNT')}
          title={tr('ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_ACCOUNT_TITLE')}
          description={tr('THIS_ACTION_IS_NOT_REVERSIBLE')}
          loading={deleteRecipientMutation.status === QueryStatus.pending}
        />
      </Box>
    </Paper>
  );
};

const cryptoStatusLabels: Record<CryptoRecipientStatus, string> = {
  [CryptoRecipientStatus.PENDING_REVIEW]: 'CRYPTO_STATUS_PENDING_REVIEW',
  [CryptoRecipientStatus.APPROVED]: 'CRYPTO_STATUS_APPROVED',
  [CryptoRecipientStatus.REJECTED]: 'CRYPTO_STATUS_REJECTED',
};

const cryptoStatusColors: Record<
  CryptoRecipientStatus,
  { foreground: string; background: string }
> = {
  [CryptoRecipientStatus.PENDING_REVIEW]: {
    foreground: 'status.pending.foreground',
    background: 'status.pending.background',
  },
  [CryptoRecipientStatus.APPROVED]: {
    foreground: 'status.completed.foreground',
    background: 'status.completed.background',
  },
  [CryptoRecipientStatus.REJECTED]: {
    foreground: 'status.failed.foreground',
    background: 'status.failed.background',
  },
};

const styles: Record<string, SystemStyleObject> = {
  iconButton: {
    color: 'text.primary',
  },
  nicknameInput: {
    '& .MuiInputBase-input': {
      py: 0.5,
      fontSize: '1rem',
    },
  },
  addNicknameButton: {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    textTransform: 'none',
    fontWeight: 500,
    px: 2,
    '&:hover': {
      backgroundColor: 'primary.dark',
    },
  },
  editNicknameButton: {
    backgroundColor: 'action.hover',
    color: 'primary.main',
    textTransform: 'none',
    fontWeight: 500,
    px: 2,
    '&:hover': {
      backgroundColor: 'action.selected',
    },
  },
  clearButton: {
    color: 'text.secondary',
    backgroundColor: 'action.hover',
    borderRadius: '50%',
    padding: '1px',
    '& svg': {
      width: '14px',
      height: '14px',
    },
    '&:hover': {
      color: 'text.primary',
      backgroundColor: 'action.selected',
    },
  },
};
